import React from "react";
import { Link } from "@inertiajs/react";
import styled from "styled-components";
import {
  brand_orange_deep,
  brand_gradient,
  outerContainerCss,
  narrowScreenMediaQuery,
} from "../constants/styles";

// Role-aware secondary navigation strip pinned beneath the global
// Header. Each tab pill is gated by the `tabs` map shipped from
// Portal::RoleSet#to_h. The active pill uses the brand gradient fill
// for instant orientation; resting pills are cream with an orange
// border. At <= 720px the strip horizontally scrolls with snap so a
// finger swipe lands on a tab.
const TAB_DEFS = [
  { key: "aktivitas", label: "Aktivitas", href: "/dashboards" },
  { key: "profil", label: "Profil", href: "/profiles" },
  { key: "langganan", label: "Membership", href: "/memberships" },
  { key: "sertifikat", label: "Sertifikat", href: "/sertifikat" },
  { key: "perusahaan", label: "Perusahaan", href: "/admin_company/dashboard" },
  { key: "laporan", label: "Laporan", href: "/admin_company/reports" },
  { key: "afiliator", label: "Afiliator", href: "/users/affiliators" },
];

function PortalSubNav({ active, tabs = {} }) {
  const visible = TAB_DEFS.filter((t) => tabs[t.key]);
  if (visible.length === 0) return null;

  return (
    <Strip aria-label="Navigasi portal">
      <Inner>
        <Scroller role="tablist">
          {visible.map((tab) => {
            const isActive = tab.key === active;
            return (
              <Tab
                key={tab.key}
                href={tab.href}
                role="tab"
                $active={isActive}
                aria-selected={isActive}
                aria-current={isActive ? "page" : undefined}
              >
                {tab.label}
              </Tab>
            );
          })}
        </Scroller>
      </Inner>
    </Strip>
  );
}

export default React.memo(PortalSubNav);

const Strip = styled.nav`
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  border-top: 1px solid rgba(216, 89, 7, 0.10);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
  position: sticky;
  top: 0;
  z-index: 20;

  [data-theme="dark"] & {
    background: linear-gradient(
      135deg,
      rgba(216, 89, 7, 0.12),
      rgba(247, 179, 12, 0.06)
    );
    border-top-color: rgba(216, 89, 7, 0.28);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
`;

const Inner = styled.div`
  ${outerContainerCss}
  padding-top: 12px;
  padding-bottom: 12px;
`;

const Scroller = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media ${narrowScreenMediaQuery} {
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    padding-bottom: 4px;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const Tab = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 10px 22px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-decoration: none;
  white-space: nowrap;
  transition:
    filter 150ms ease,
    transform 100ms ease,
    background 150ms ease,
    border-color 150ms ease;
  flex-shrink: 0;
  scroll-snap-align: start;

  background: ${({ $active }) =>
    $active ? brand_gradient : "rgba(255, 255, 255, 0.65)"};
  color: ${({ $active }) => ($active ? "#ffffff" : brand_orange_deep)};
  border: 1px solid
    ${({ $active }) => ($active ? "transparent" : "rgba(216, 89, 7, 0.28)")};
  box-shadow: ${({ $active }) =>
    $active ? "0 6px 18px rgba(216, 89, 7, 0.25)" : "none"};

  &:hover {
    filter: brightness(0.97);
    transform: translateY(-1px);
    color: ${({ $active }) => ($active ? "#ffffff" : brand_orange_deep)};
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }

  [data-theme="dark"] & {
    background: ${({ $active }) =>
      $active ? brand_gradient : "rgba(255, 255, 255, 0.05)"};
    color: ${({ $active }) => ($active ? "#ffffff" : "#fdba74")};
    border-color: ${({ $active }) =>
      $active ? "transparent" : "rgba(216, 89, 7, 0.35)"};
    box-shadow: ${({ $active }) =>
      $active ? "0 6px 18px rgba(216, 89, 7, 0.4)" : "none"};
  }
`;
